import React from 'react'
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechFormLookup from '@agtech/web/components/Forms/Controls/Lookups/AgTechFormLookup';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { usePlanningTaskAssignAction, PlanningTaskAssignActionDTO } from '@sales/data/planningTasks/PlanningTaskDataActions';
import { PlanningTaskDataGetSaleTasksQuery } from '@sales/data/planningTasks/PlanningTaskQueries';
import { OperationDataGetUsersQuery } from '@sales/data/operation/users/OperationUserQueries';

export type AssignPlanningTaskDialogProps = {
    saleId: number,
    taskId: number
}

const AssignPlanningTaskDialog = (props: AssignPlanningTaskDialogProps) => {
    const dialogQuery = useAgTechQuery({
        query: PlanningTaskDataGetSaleTasksQuery,
        queryContext: { saleId: props.saleId }
    });

    const usersQuery = useAgTechQuery({
        query: OperationDataGetUsersQuery,
        projection: users => Object.values(users)
    });

    const dialogAction = usePlanningTaskAssignAction();

    return (
        <AgTechFormDialog
            width={450}
            structure={() => ({
                header: 'Assign Planning Task',
                submitButtonText: 'Assign',
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: tasks => ({
                    task: tasks[props.taskId],
                    userId: tasks[props.taskId]?.assignedToUserId ?? null
                } as PlanningTaskAssignActionDTO)
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d
            }}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <AgTechFormLookup
                            entityType={formContext.entity}
                            control={{
                                caption: 'Assigned To',
                                width: 100
                            }}
                            selection={{
                                selectionControlTitle: 'Users',
                                data: {
                                    itemQuery: usersQuery,
                                    selectedItemId: formContext.entity.userId?.toString(),
                                    searchItems: (item, filter) => `${item.firstName} ${item.lastName}`.toLowerCase().includes(filter.toLowerCase()),
                                },
                                items: {
                                    itemText: u => `${u.firstName} ${u.lastName}`,
                                    itemHeight: 60,
                                    itemRendering: u => (
                                        <AgTechTitleDetailHeader
                                            title={`${u.firstName} ${u.lastName}`}
                                            detail={u.email}
                                            titleFontSize='font-size-small'
                                            detailFontSize='font-size-very-small'
                                            detailClasses='mt-1-half'
                                        />
                                    ),
                                },
                                behavior: {
                                    onItemSelected: async (e, selectedUser) => {
                                        return {
                                            ...formContext.entity,
                                            userId: selectedUser?.id ?? null
                                        };
                                    },
                                },
                                popup: {
                                    height: 240,
                                    width: 360
                                }
                            }}
                        />
                    </AgTechFormRow>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

AssignPlanningTaskDialog.Button = (props: AssignPlanningTaskDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text='Assign'
        icon='fas fa-user-plus'
        {...props}
        action={e => AssignPlanningTaskDialog.Open(e, props)}
    />
);

AssignPlanningTaskDialog.Open = (e: AgTechClickEventHandler, props: AssignPlanningTaskDialogProps) => e.dialogs.openDialog({
    dialog: () => <AssignPlanningTaskDialog saleId={props.saleId} taskId={props.taskId} />
});

export default AssignPlanningTaskDialog;
